import { oc } from "@orpc/contract";
import { z } from "zod";
import { credentialAutoPauseThresholdSchema } from "@erp/shared-types";

/**
 * Supported credential types (mirrors backend CredentialType enum).
 */
export const CredentialTypeSchema = z.enum([
  "password",
  "totp",
  "auth_state",
  "api_token",
  "oauth_client",
  "certificate",
  "azure_blob",
  "aws",
]);

/**
 * Summary of a credential (secret value redacted).
 */
export const CredentialSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: CredentialTypeSchema,
  /** For Password type: masked username. Null otherwise. */
  usernameMasked: z.string().nullable(),
  autoPauseAfterRejectedRuns: credentialAutoPauseThresholdSchema,
  consecutiveRejectedRuns: z.number().int().nonnegative(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Create a new credential.
 */
export const CreateCredentialInputSchema = z.object({
  autoPauseAfterRejectedRuns: credentialAutoPauseThresholdSchema.optional(),
  organizationId: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must not exceed 255 characters"),
  type: CredentialTypeSchema,
  /** The secret value (password, token, etc.) — never returned by the API */
  secret: z.string().min(1, "Secret is required"),
  /** Type-specific metadata (e.g. { username } for Password) */
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Update an existing credential.
 */
export const UpdateCredentialInputSchema = z.object({
  autoPauseAfterRejectedRuns: credentialAutoPauseThresholdSchema.optional(),
  organizationId: z.string().uuid(),
  credentialId: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  /** If provided, replaces the stored secret */
  secret: z.string().min(1).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  metadataPatch: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Reference a single credential.
 */
export const CredentialByIdInputSchema = z.object({
  organizationId: z.string().uuid(),
  credentialId: z.string().uuid(),
});

/**
 * List credentials with optional type filter.
 */
export const ListCredentialsInputSchema = z.object({
  organizationId: z.string().uuid(),
  type: CredentialTypeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const ListCredentialsResponseSchema = z.object({
  credentials: z.array(CredentialSummarySchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

export const DeleteCredentialResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * Generic Credentials CRUD Contracts
 */
export const credentialsContracts = {
  create: oc
    .input(CreateCredentialInputSchema)
    .output(CredentialSummarySchema)
    .route({
      method: "POST",
      summary: "Store a new credential",
      description:
        "Saves an encrypted secret (password, TOTP seed, API token, certificate, cloud key) with metadata for automations to use. The secret is never returned.",
    }),

  update: oc
    .input(UpdateCredentialInputSchema)
    .output(CredentialSummarySchema)
    .route({
      method: "POST",
      summary: "Update a stored credential",
      description:
        "Renames a credential or changes its metadata; a supplied secret replaces the stored one. Returns the redacted credential.",
    }),

  get: oc
    .input(CredentialByIdInputSchema)
    .output(CredentialSummarySchema.nullable())
    .route({
      method: "GET",
      summary: "Get a stored credential",
      description:
        "Returns one credential with its secret redacted and username masked, or null when it does not exist.",
    }),

  list: oc
    .input(ListCredentialsInputSchema)
    .output(ListCredentialsResponseSchema)
    .route({
      method: "GET",
      summary: "List stored credentials",
      description:
        "Returns the organization's credentials with secrets redacted, optionally filtered by type, with pagination.",
    }),

  delete: oc
    .input(CredentialByIdInputSchema)
    .output(DeleteCredentialResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a stored credential",
      description:
        "Deletes the credential and erases its stored secret; automations that use it stop authenticating.",
    }),
} as const;

export type CredentialSummaryResponse = z.infer<typeof CredentialSummarySchema>;
export type CreateCredentialInput = z.infer<typeof CreateCredentialInputSchema>;
export type UpdateCredentialInput = z.infer<typeof UpdateCredentialInputSchema>;
export type ListCredentialsInput = z.infer<typeof ListCredentialsInputSchema>;
export type ListCredentialsResponse = z.infer<
  typeof ListCredentialsResponseSchema
>;
